const DEBUG = false; // Colorizes the image into the different sections
const RGBA_BIT_PER_PIXEL = 4; // RGBA
// Used to generate the Picture
const DETECTION_PATTERN = [
  [1, 1, 1, 1, 1, 0, 1],
  [1, 0, 0, 1, 0, 0, 1],
  [1, 1, 0, 1, 1, 1, 1],
  [0, 1, 1, 0, 1, 0, 0],
  [1, 0, 1, 0, 1, 0, 0],
  [0, 0, 0, 0, 1, 0, 1],
  [1, 0, 0, 0, 0, 1, 1],
];
const DETECTION_PATTERN_WIDTH = DETECTION_PATTERN[0].length;
const DETECTION_PATTERN_HEIGHT = DETECTION_PATTERN.length;
const SIZE_PATTERN_WIDTH = 2;
const HASH_BITS = 32;

export function generateWhispSignalImage(
  messageString: string,
  color: [number, number, number],
  bitsPerColumn: number = 6
): ImageData {
  return new WhispSignalGenerator(
    messageString,
    color,
    bitsPerColumn
  ).generateSignalImage();
}

class WhispSignalGenerator {
  constructor(
    private messageString: string,
    private color: [number, number, number],
    private bitsPerColumn: number
  ) {
    if (messageString.length >= 1024)
      throw new Error("Message must be shorter than 1024 characters.");

    if (bitsPerColumn < 7) this.imageHeight = 7;
    else this.imageHeight = bitsPerColumn;

    if (!color.every((col) => col >= 0 && col <= 255)) {
      throw new Error("Color is RGB and requires values between 0 and 255");
    }
  }

  private imageHeight: number;

  getWidht(): number {
    return (
      DETECTION_PATTERN_WIDTH + SIZE_PATTERN_WIDTH + this.requiredWidthForData()
    );
  }

  getHeight(): number {
    return this.imageHeight;
  }

  private requiredWidthForData(): number {
    return Math.ceil(
      (this.messageString.length * 8 + HASH_BITS) / this.bitsPerColumn
    );
  }

  generateSignalImage(): ImageData {
    const width = this.getWidht();
    const height = this.imageHeight;
    const image = new Uint8ClampedArray(width * height * RGBA_BIT_PER_PIXEL);
    const serializer = new DataToImageSerializer(
      image,
      this.bitsPerColumn,
      height,
      width,
      this.color
    );

    // ----------------------------------------------------------------
    // Serialize the detection pattern
    this.serializePattern(serializer, DEBUG ? [0, 0, 255] : undefined);

    // ----------------------------------------------------------------
    // Move to size pattern position - starts one row after detection pattern and then goes in -x direction
    serializer.goTo(DETECTION_PATTERN_WIDTH + SIZE_PATTERN_WIDTH - 1, 0);

    // SizePattern - Height of the pattern - as 0 is not a valid value, all values here are +1 (so pattern height of 8 should be a 7)
    serializer.writeValue(
      this.bitsPerColumn - 1,
      4,
      DETECTION_PATTERN_HEIGHT,
      DEBUG ? [0, 255, 0] : undefined
    );
    // SizePattern - Lenght of data, going -x while serializing
    serializer.writeValueReverseY(
      this.messageString.length,
      10,
      DETECTION_PATTERN_HEIGHT,
      DEBUG ? [255, 0, 0] : undefined
    );

    // ----------------------------------------------------------------
    // Move to Data Pattern, starts one row after detection pattern and writes in +x direction
    serializer.goTo(DETECTION_PATTERN_WIDTH + SIZE_PATTERN_WIDTH, 0);

    for (const char of this.messageString) {
      const charByte = char.charCodeAt(0);
      serializer.writeValue(charByte, 8);
    }

    // ----------------------------------------------------------------
    // Lastly write the data hash
    const dataHash = djb2Digest(this.messageString);
    serializer.writeValue(
      dataHash,
      HASH_BITS,
      undefined,
      DEBUG ? [0, 255, 255] : undefined
    );

    // serializer.printCurrentData();

    return new ImageData(image, width, height);
  }

  private serializePattern(
    serializer: DataToImageSerializer,
    color?: [number, number, number]
  ) {
    let yIndex = 0;
    for (const row of DETECTION_PATTERN) {
      for (const bit of row) {
        if (bit) serializer.paintCurrentPixel(color);
        serializer.advanceX();
      }
      yIndex++;
      serializer.goTo(0, yIndex);
    }
    // Move to end of pattern
    serializer.goTo(DETECTION_PATTERN_WIDTH, 0);
  }
}

class DataToImageSerializer {
  private dataCursor = 0;
  private currentY = 0;
  private currentX = 0;

  constructor(
    private targetImageData: Uint8ClampedArray,
    private maxY: number,
    private imageHeight: number,
    private imageWidth: number,
    private targetColor: [number, number, number]
  ) {}

  /**
   * Writes the given data with the given amount of bits to the image.
   * Advances from top to botton, left to right for every bit
   * @param value target value
   * @param bits amount of bits for value
   * @param maxY override the maximum Y value by which the X column should be moved
   * @param color override the color of the pixel drawn by this function
   */
  writeValue(
    value: number,
    bits: number,
    maxY?: number,
    overrideColor?: [number, number, number]
  ) {
    for (const [bit] of iterateBits(value, bits)) {
      if (bit) this.paintCurrentPixel(overrideColor);
      this.advanceY(maxY);
    }
  }

  /**
   * Writes the given data with the given amount of bits to the image.
   * Advances from  top to bottom, right to left - instead of left to right
   * @param value target value
   * @param bits amount of bits for value
   * @param maxY override the maximum Y value by which the X column should be moved
   * @param color override the color of the pixel drawn by this function
   */
  writeValueReverseY(
    value: number,
    bits: number,
    maxY?: number,
    overrideColor?: [number, number, number]
  ) {
    for (const [bit] of iterateBits(value, bits)) {
      if (bit) this.paintCurrentPixel(overrideColor);
      this.reverseY(maxY);
    }
  }

  printCurrentData() {
    const width = this.imageWidth;
    const height = this.imageHeight;
    const data = this.targetImageData;

    let image = "";
    for (let y = 0; y < height; y++) {
      let row = "";
      for (let x = 0; x < width; x++) {
        const pixelIndex = (y * width + x) * RGBA_BIT_PER_PIXEL;
        const pixelValue = data[pixelIndex] > 0 ? 1 : 0; // Assuming pixel color is non-zero for '1'
        row += pixelValue + " ";
      }
      image += row.trim() + "\n";
    }
    console.log(image.trim());
  }

  paintCurrentPixel(color: [number, number, number] = this.targetColor) {
    this.targetImageData[this.dataCursor] = color[0]; //R
    this.targetImageData[this.dataCursor + 1] = color[1]; //G
    this.targetImageData[this.dataCursor + 2] = color[2]; //B
    this.targetImageData[this.dataCursor + 3] = 255; //A
  }

  /**
   * Advances our cursor to the next row, or if we've reached the end of the image to the next column
   */
  advanceY(maxY: number = this.maxY) {
    if (++this.currentY >= maxY) {
      this.currentX++;
      this.currentY = 0;
    }

    this.moveCursorTo(this.currentX, this.currentY);
  }

  /**
   * Advances our cursor to the next row, or if we've reached the end of the image to the next column
   */
  reverseY(maxY: number = this.maxY) {
    if (++this.currentY >= maxY) {
      this.currentX--;
      this.currentY = 0;
    }

    this.moveCursorTo(this.currentX, this.currentY);
  }

  /**
   * Advances our cursor to the next column
   */
  advanceX() {
    this.moveCursorTo(++this.currentX, this.currentY);
  }

  /**
   * Moves our cursor to Y0 X++
   */
  skipX() {
    this.currentY = 0;
    this.moveCursorTo(++this.currentX, this.currentY);
  }

  /**
   * Goes to given coordinates
   */
  goTo(x: number, y: number) {
    this.currentX = x;
    this.currentY = y;
    this.moveCursorTo(this.currentX, this.currentY);
  }

  /**
   * Moves the cursor to the given coordinates
   */
  private moveCursorTo(x: number, y: number) {
    this.dataCursor = (y * this.imageWidth + x) * RGBA_BIT_PER_PIXEL;
  }
}

function* iterateBits(value: number, bits: number) {
  for (let currentBit = 0; currentBit < bits; currentBit++) {
    yield [(value >> currentBit) & 1, currentBit] as [
      bit: 0 | 1,
      index: number
    ];
  }
}

function djb2Digest(str: string) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash >>> 0) * 33 + str.charCodeAt(i);
  }
  return hash >>> 0;
}
